import dayjs from "@shared/dayjs";

import type { DateRange, DateRangeOptionValue } from "./types";

const ISO_DATE_FORMAT = "YYYY-MM-DD";

const toIso = (date: dayjs.Dayjs): string => date.format(ISO_DATE_FORMAT);

/**
 * Resolve a canonical option value to a concrete `{ startAt, endAt }` date range.
 *
 * Current-period options (`this_week`, `this_month`, `this_quarter`, `this_year`)
 * respect `disableFutureDates`: when `true`, the range truncates at today
 * (period-to-date); otherwise it covers the full calendar period.
 *
 * @param value - The selected option value.
 * @param options.now - Override for “today”. Defaults to `new Date()`.
 * @param options.disableFutureDates - See above. Defaults to `false`.
 * @param options.customRange - Required when `value === "custom"`. Returned as-is.
 *
 * @returns A `DateRange` with ISO `yyyy-MM-dd` strings, or `null`s for "all_time".
 */
export const resolveDateRange = (
  value: DateRangeOptionValue,
  options: { now?: Date; customRange?: DateRange; disableFutureDates?: boolean } = {},
): DateRange => {
  const now = dayjs(options.now);
  const disableFutureDates = !!options.disableFutureDates;

  switch (value) {
    case "all_time":
      return { startAt: null, endAt: null };
    case "today":
      return { startAt: toIso(now), endAt: toIso(now) };
    case "yesterday": {
      const y = now.subtract(1, "day");
      return { startAt: toIso(y), endAt: toIso(y) };
    }
    case "this_week": {
      // Monday-start week. Shift `dayjs.day()` (0=Sun…6=Sat) to Mon=0…Sun=6.
      const daysFromMonday = (now.day() + 6) % 7;
      const start = now.subtract(daysFromMonday, "day");
      return {
        startAt: toIso(start),
        endAt: toIso(disableFutureDates ? now : start.add(6, "day")),
      };
    }
    case "last_week": {
      const daysFromMonday = (now.day() + 6) % 7;
      const startOfThisWeek = now.subtract(daysFromMonday, "day");
      return {
        startAt: toIso(startOfThisWeek.subtract(7, "day")),
        endAt: toIso(startOfThisWeek.subtract(1, "day")),
      };
    }
    // `last_N_days` spans EXACTLY N calendar days, today inclusive, matching
    // `Reporting.start_and_end_for_range`. Note the `N - 1`: subtracting a full
    // N would produce an N+1 day window and inflate every total.
    case "last_7_days":
      return { startAt: toIso(now.subtract(6, "day")), endAt: toIso(now) };
    case "last_14_days":
      return { startAt: toIso(now.subtract(13, "day")), endAt: toIso(now) };
    case "last_30_days":
      return { startAt: toIso(now.subtract(29, "day")), endAt: toIso(now) };
    case "last_90_days":
      return { startAt: toIso(now.subtract(89, "day")), endAt: toIso(now) };
    case "this_month":
      return {
        startAt: toIso(now.startOf("month")),
        endAt: toIso(disableFutureDates ? now : now.endOf("month")),
      };
    case "last_month": {
      const lastMonth = now.subtract(1, "month");
      return { startAt: toIso(lastMonth.startOf("month")), endAt: toIso(lastMonth.endOf("month")) };
    }
    case "this_quarter":
      return {
        startAt: toIso(now.startOf("quarter")),
        endAt: toIso(disableFutureDates ? now : now.endOf("quarter")),
      };
    case "last_quarter": {
      const lastQuarter = now.subtract(3, "month");
      return {
        startAt: toIso(lastQuarter.startOf("quarter")),
        endAt: toIso(lastQuarter.endOf("quarter")),
      };
    }
    case "this_year":
      return {
        startAt: toIso(now.startOf("year")),
        endAt: toIso(disableFutureDates ? now : now.endOf("year")),
      };
    case "last_year": {
      const lastYear = now.subtract(1, "year");
      return { startAt: toIso(lastYear.startOf("year")), endAt: toIso(lastYear.endOf("year")) };
    }
    case "custom":
      return options.customRange ?? { startAt: null, endAt: null };
    default: {
      // Exhaustiveness guard — if a new option is added to the union, TS will error here.
      const exhaustiveCheck: never = value;
      throw new Error(`Unhandled DateRangeOptionValue: ${String(exhaustiveCheck)}`);
    }
  }
};
